var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { appStore } from "../state/store";
export class GeneratedSchedule {
    constructor(app) {
        this.app = app;
    }
    normalizeScheduleData(data) {
        var _a, _b;
        if (!data || typeof data !== 'object') {
            throw new Error("AI returned an invalid or non-object response.");
        }
        if (Array.isArray(data.daily_schedule)) {
            return data;
        }
        console.warn("[Normalization] 'daily_schedule' key was not a valid array. Attempting fallbacks...");
        const alternativeKeys = ['schedule', 'days'];
        for (const key of alternativeKeys) {
            if (Array.isArray(data[key])) {
                console.warn(`[Normalization] Found data under alternative key: '${key}'. Correcting structure.`);
                return Object.assign(Object.assign({}, data), { daily_schedule: data[key] });
            }
        }
        if (Array.isArray(data)) {
            console.warn("[Normalization] AI returned a root array. Wrapping it in the correct structure.");
            return {
                alignmentScore: ((_a = data[0]) === null || _a === void 0 ? void 0 : _a.alignmentScore) || 75,
                rationale: ((_b = data[0]) === null || _b === void 0 ? void 0 : _b.rationale) || "AI failed to provide a rationale.",
                daily_schedule: data
            };
        }
        throw new Error("AI returned a malformed schedule structure that could not be normalized.");
    }
    fetchAndRenderSchedule() {
        return __awaiter(this, void 0, void 0, function* () {
            const container = document.getElementById('schedule-container');
            if (!container)
                return;
            const state = appStore.getState();
            const selectedArchetype = state.mainAppState.currentState.selectedScheduleArchetype;
            if (!selectedArchetype) {
                this.renderError("No schedule archetype selected.");
                return;
            }
            try {
                /**
                 * ARCHITECTURAL SYNERGY (Caching): This component reliably consumes the
                 * `diagnosticContext` which was generated and cached by the `OnboardingComplete`
                 * component. Because the analysis result is now cached in the `appStore`,
                 * we are guaranteed to have this context available without needing to make
                 * a redundant, expensive API call. This makes this component faster, more
                 * robust, and ensures narrative continuity in the user experience.
                 */
                const analysis = state.user.holisticStoryAnalysis;
                let therapeuticHabitImplementation = null;
                if (analysis && analysis.diagnosticContext) {
                    therapeuticHabitImplementation = analysis.diagnosticContext.prescribedHabit;
                }
                else {
                    // Fallback for robustness
                    console.warn("Holistic analysis/context not found. Using default therapeutic habit.");
                    therapeuticHabitImplementation = state.knowledgeGraph.neuroticism.disadvantages.highAnxietyImpairingPerformance.mitigatingTraits.conscientiousness.implementations[0];
                }
                const API_URL = process.env.API_BASE_URL;
                const response = yield fetch(`${API_URL}/api/generate-schedule`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        projects: state.user.onboardingInputs.personalProjects,
                        bfiProfile: state.user.profile.bfiProfile,
                        archetype: selectedArchetype,
                        lifePriorities: state.user.onboardingInputs.lifePriorities,
                        personalValues: state.user.onboardingInputs.personalValues,
                        workStyleArchetype: state.user.profile.workStyleArchetype,
                        belongingSense: state.user.onboardingInputs.belongingSense,
                        therapeuticHabit: therapeuticHabitImplementation
                    }),
                });
                if (!response.ok)
                    throw new Error(`AI service failed: ${response.status}`);
                const rawData = yield response.json();
                const scheduleData = this.normalizeScheduleData(rawData);
                appStore.setGeneratedSchedule(scheduleData);
                this.renderSuccess(scheduleData);
            }
            catch (e) {
                this.renderError(e.message);
            }
        });
    }
    renderSuccess(scheduleData) {
        var _a;
        const container = document.getElementById('schedule-container');
        if (!container)
            return;
        const selectedArchetype = appStore.getState().mainAppState.currentState.selectedScheduleArchetype;
        const archetype = selectedArchetype ? selectedArchetype.charAt(0).toUpperCase() + selectedArchetype.slice(1) : "Custom";
        const scheduleHtml = scheduleData.daily_schedule.map(day => {
            var _a;
            return `
            <div class="day-column">
                <h4>${day.day}</h4>
                <div class="day-events">
                    ${((_a = day.events) === null || _a === void 0 ? void 0 : _a.map(item => {
                const link = item.goldenThreadLink;
                const habitDerivation = item.derivation;
                let linkHtml = '';
                if (link) {
                    const tooltipContent = `Source: ${link.sourceName} (${link.sourceType})&#10;Rationale: ${link.rationale}`;
                    linkHtml = `<div class="golden-thread-link" title="${tooltipContent}">🔗</div>`;
                }
                let habitLinkHtml = '';
                if (habitDerivation) {
                    const tooltipContent = `Therapeutic Rationale:&#10;${habitDerivation.rationale}`;
                    habitLinkHtml = `<div class="habit-implementation-link" title="${tooltipContent}">🧠</div>`;
                }
                return `
                            <div class="schedule-item-small">
                                <div class="item-content">
                                    <strong>${item.startTime}-${item.endTime}:</strong> ${item.title}
                                </div>
                                ${habitLinkHtml}
                                ${linkHtml}
                            </div>
                        `;
            }).join('')) || '<p>No events scheduled.</p>'}
                </div>
            </div>
        `;
        }).join('');
        const formattedRationale = scheduleData.rationale.replace(/\n/g, '<br>');
        container.innerHTML = `
            <div class="alignment-score-widget">
                <span>This Week's Alignment Score</span>
                <div class="score" title="The percentage of your week aligned with your core priorities, values, and projects.">${scheduleData.alignmentScore}%</div>
            </div>
            <h2>Your Optimized Week (${archetype})</h2>
            <p>This is the <strong>Quantified Strategic Output</strong> of your week, with a transparent <strong>Causal</strong> link to your identity.</p>
            <div class="schedule-grid">${scheduleHtml}</div>
            <div class="ai-rationale">
                <h3 class="serif-heading">System Rationale for the Week:</h4>
                <p>${formattedRationale}</p>
            </div>
            <div class="button-container">
                <button id="accept-schedule" class="button-primary">Accept & Start My Week</button>
            </div>
        `;
        (_a = document.getElementById('accept-schedule')) === null || _a === void 0 ? void 0 : _a.addEventListener('click', () => {
            appStore.acceptSchedule();
            this.app.returnToDashboard();
        });
    }
    renderError(errorMessage) {
        var _a;
        const container = document.getElementById('schedule-container');
        if (!container)
            return;
        container.innerHTML = `<p class="error"><strong>An error occurred while generating your schedule:</strong><br/>${errorMessage}</p><button id="return-dashboard" class="button-secondary">Return to Dashboard</button>`;
        (_a = document.getElementById('return-dashboard')) === null || _a === void 0 ? void 0 : _a.addEventListener('click', () => {
            this.app.returnToDashboard();
        });
    }
    render() {
        return `
            <div class="card wide-card" id="schedule-container">
                <div class="loader"></div>
                <p>Architectural Engine is designing your week...</p>
                <p>This could take some thime</p>
            </div>
        `;
    }
    attachEventListeners() {
        this.fetchAndRenderSchedule();
    }
}
